import { DatasourceDto } from "#/services/pilotService/types";
import { Action as ActionType } from "@iva/store-utils";

export enum ColorScheme {
    OS = "OS",
    DARK = "DARK",
    LIGHT = "LIGHT",
}

export type UserPreferences = {
    theme: ColorScheme;
};
export type AppDrawerState = {
    visible: boolean;
};

/**
 * TODO replace with types from pilot client lib
 */
export enum QueryLanguage {
    SQL = "sql",
}

export enum HistoryDataTypeEnum {
    UNKNOWN,
    INTERPRETATION,
    TABLE,
    ACTION,
    OPERATOR_INVOCATION,
}
export enum InteractionType {
    SELECT,
    HIGHLIGHT,
}

export type HistoryItem = {
    /**
     * Date of interaction
     */
    timestamp: number;
    /**
     * Type of element interacted with
     */
    type: HistoryDataTypeEnum;
    /**
     * unique identifier for the element
     */
    id: number;
    /**
     * type of interaction
     */
    actionType: InteractionType;
};

export type Session = {
    label: string;
    interpretations: number[];
};

// State definition
export interface UiState {
    savedSessions: Session[];
    selectedDatasource: DatasourceDto;
    selectedQueryLanguage: QueryLanguage;
    theme: ColorScheme;
    appDrawer: AppDrawerState;
    rid: string;
    maxInterpretationsLimit: number;
    rowsPerTableLimit: number;
    activeInterpretations: number[];
    favoriteColumns: string[];
    doiWeights: number[];
    query: string;
    history: HistoryItem[];
}

// Action types definition
export enum UiStateActionTypeIdentifiers {
    SAVE_SESSION = "@@applicationState/SAVE_SESSION",
    LOAD_SESSION = "@@applicationState/LOAD_SESSION",
    DELETE_SESSION = "@@applicationState/DELETE_SESSION",
    SET_THEME = "@@applicationState/SET_THEME",
    TOGGLE_DRAWER = "@@applicationState/SET_APP_DRAWER_VISIBILITY",
    SET_INTERPRETATIONS_LIMIT = "@@applicationState/SET_INTERPRETATIONS_LIMIT",
    SET_ROWS_PER_TABLE_LIMIT = "@@applicationState/SET_ROWS_PER_TABLE_LIMIT",
    TOGGLE_TABLE_FAV_STATE = "@@applicationStat/TOGGLE_TABLE_FAV_STATE",
    SET_TABLE_FAV_STATE = "@@applicationStat/SET_TABLE_FAV_STATE",
    SET_USER_ID = "@@applicationState/SET_USER_ID",
    TOGGLE_COLUMN_FAV_STATE = "@@applicationState/TOGGLE_COLUMN_FAV_STATE",
    SET_COLUMN_FAV_STATE = "@@applicationState/SET_COLUMN_FAV_STATE",
    SET_RELEVANCE_WEIGHT = "@@applicationState/SET_RELEVANCE_WEIGHT",
    SET_COMPARISON_WEIGHT = "@@applicationState/SET_COMPARISON_WEIGHT",
    SET_QUERY_WEIGHT = "@@applicationState/SET_QUERY_WEIGHT",
    SET_FAVORITE_WEIGHT = "@@applicationState/SET_FAVORITE_WEIGHT",
    SET_QUERY_STRING = "@@applicationState/SET_QUERY_STRING",
    SET_QUERY_LANGUAGE = "@@applicationState/SET_QUERY_LANGUAGE",
    SET_DATASOURCE = "@@applicationState/SET_DATASOURCE",
    ADD_HISTORY_ITEM = "@@applicationState/ADD_HISTORY_ITEM",
    ADD_HISTORY_ITEMS = "@@applicationState/ADD_HISTORY_ITEMS",
}

// Actions definition
export type SaveSessionAction = ActionType<
    UiStateActionTypeIdentifiers.SAVE_SESSION,
    Session
>;
export type LoadSessionAction = ActionType<
    UiStateActionTypeIdentifiers.LOAD_SESSION,
    string
>;
export type DeleteSessionAction = ActionType<
    UiStateActionTypeIdentifiers.DELETE_SESSION,
    string
>;

export type SetThemeAction = ActionType<
    UiStateActionTypeIdentifiers.SET_THEME,
    ColorScheme
>;

export type ToggleAppDrawerVisibilityAction =
    ActionType<UiStateActionTypeIdentifiers.TOGGLE_DRAWER>;

export type SetInterpretationsLimitAction = ActionType<
    UiStateActionTypeIdentifiers.SET_INTERPRETATIONS_LIMIT,
    number
>;

export type SetRowsPerTableLimitAction = ActionType<
    UiStateActionTypeIdentifiers.SET_ROWS_PER_TABLE_LIMIT,
    number
>;

export type ToggleTableFavStateAction = ActionType<
    UiStateActionTypeIdentifiers.TOGGLE_TABLE_FAV_STATE,
    number
>;

export type SetTableFavStateAction = ActionType<
    UiStateActionTypeIdentifiers.SET_TABLE_FAV_STATE,
    { id: number; value: boolean }
>;

export type SetUserIdAction = ActionType<
    UiStateActionTypeIdentifiers.SET_USER_ID,
    { value: string }
>;

export type ToggleColumnFavStateAction = ActionType<
    UiStateActionTypeIdentifiers.TOGGLE_COLUMN_FAV_STATE,
    string
>;

export type SetColumnFavStateAction = ActionType<
    UiStateActionTypeIdentifiers.SET_COLUMN_FAV_STATE,
    { column: string; value: boolean }
>;

export type SetRelevanceWeightAction = ActionType<
    UiStateActionTypeIdentifiers.SET_RELEVANCE_WEIGHT,
    number
>;

export type SetComparisonWeightAction = ActionType<
    UiStateActionTypeIdentifiers.SET_COMPARISON_WEIGHT,
    number
>;

export type SetQueryWeightAction = ActionType<
    UiStateActionTypeIdentifiers.SET_QUERY_WEIGHT,
    number
>;

export type SetFavoriteWeightAction = ActionType<
    UiStateActionTypeIdentifiers.SET_FAVORITE_WEIGHT,
    number
>;

export type SetQueryAction = ActionType<
    UiStateActionTypeIdentifiers.SET_QUERY_STRING,
    string
>;

export type SetQueryLanguageAction = ActionType<
    UiStateActionTypeIdentifiers.SET_QUERY_LANGUAGE,
    QueryLanguage
>;

export type SetDatasourceAction = ActionType<
    UiStateActionTypeIdentifiers.SET_DATASOURCE,
    DatasourceDto
>;
export type AddHistoryItem = ActionType<
    UiStateActionTypeIdentifiers.ADD_HISTORY_ITEM,
    HistoryItem
>;
export type AddHistoryItems = ActionType<
    UiStateActionTypeIdentifiers.ADD_HISTORY_ITEMS,
    HistoryItem[]
>;

export type UiStateActionTypes =
    | SaveSessionAction
    | LoadSessionAction
    | DeleteSessionAction
    | SetDatasourceAction
    | SetQueryLanguageAction
    | SetQueryAction
    | SetThemeAction
    | ToggleAppDrawerVisibilityAction
    | SetInterpretationsLimitAction
    | SetRowsPerTableLimitAction
    | ToggleTableFavStateAction
    | SetTableFavStateAction
    | SetUserIdAction
    | ToggleColumnFavStateAction
    | SetColumnFavStateAction
    | SetRelevanceWeightAction
    | SetComparisonWeightAction
    | SetQueryWeightAction
    | SetFavoriteWeightAction
    | AddHistoryItem
    | AddHistoryItems;
